import { authService } from '@/api/auth.service';
import { usersService } from '@/api/users.service';
import type { CreatePartnerSubTypeDto, UpdatePartnerSubTypeDto } from '@carvia/ros-client-types';
import type {
  CreateCustomerUserDto,
  CreateUserDto,
  FindAllUsersQueryDto,
  UpdateUserDto,
  User,
} from '@/entities/auth/user.entity';
import { removeItemFromQueriesData, updateItemOnQueriesData } from '@/utils/update-query-data';
import { LEADS_QUERY_KEY } from '@/queries/use-leads';

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { type MaybeRefOrGetter, toRef, toValue } from 'vue';

const QUERY_KEY = 'users';
const PARTNER_SUB_TYPES_QUERY_KEY = 'partner-sub-types';

export const useMe = () => {
  return useQuery({ queryKey: ['me'], queryFn: () => authService.getMe() });
};

export const useUser = (
  id: MaybeRefOrGetter<string>,
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  return useQuery({
    queryKey: [QUERY_KEY, toRef(id)],
    queryFn: () => usersService.getOne(toValue(id)),
    enabled: toValue(enabled),
  });
};

export const useUserWithEmailIfExists = (
  email: MaybeRefOrGetter<string>,
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  return useQuery({
    queryKey: [QUERY_KEY, toRef(email)],
    queryFn: () => usersService.getOneByEmailIfExists(toValue(email)),
    enabled: () => toValue(enabled),
  });
};

export const useUsers = (limit = 10) => {
  return useInfiniteQuery({
    queryKey: [QUERY_KEY],
    queryFn: ({ pageParam = 1 }) => usersService.getAllPaginated({ page: pageParam, limit }),
    getNextPageParam: (lastPage) => lastPage.meta.nextPage,
    initialPageParam: 1,
  });
};

export const useUsersWithQuery = (
  query: MaybeRefOrGetter<FindAllUsersQueryDto>,
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  return useInfiniteQuery({
    queryKey: [QUERY_KEY, toRef(query)],
    queryFn: ({ pageParam = 1 }) =>
      usersService.getAllWithQuery({ ...toValue(query), page: pageParam }),
    enabled: toValue(enabled),
    getNextPageParam: (lastPage) => lastPage.meta.nextPage,
    initialPageParam: 1,
  });
};

export const useUpdateUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [QUERY_KEY],
    mutationFn: ({ id, user }: { id: string; user: UpdateUserDto }) =>
      usersService.update(id, user),
    onMutate: () => ({
      successMessage: 'User updated',
      catchApprovalRequests: true,
      invalidateQueryKeys: null,
    }),
    onSuccess: (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(result, variables, onMutateResult, context);
      queryClient.invalidateQueries({ queryKey: ['additional-costs', variables.id] }).then();
      updateItemOnQueriesData<User>([QUERY_KEY], result, queryClient);
    },
  });
};

export const useDeleteUserPermanently = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [QUERY_KEY],
    mutationFn: (id: string) => usersService.deletePermanently(id),
    onMutate: () => ({ successMessage: 'Customer account deleted', invalidateQueryKeys: null }),
    onSuccess: (result, id, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(result, id, onMutateResult, context);
      removeItemFromQueriesData<User>([QUERY_KEY], id, queryClient);
    },
  });
};

export const useCreateUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [QUERY_KEY],
    mutationFn: (user: CreateUserDto) => usersService.create(user),
    onMutate: () => ({ successMessage: 'User created', catchApprovalRequests: true }),
    onSuccess: (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(result, variables, onMutateResult, context);
      if (variables.leadId) {
        queryClient.invalidateQueries({ queryKey: [LEADS_QUERY_KEY] });
      }
    },
  });
};

export const useCreateCustomerUser = () => {
  return useMutation({
    mutationKey: [QUERY_KEY],
    mutationFn: (user: CreateCustomerUserDto) => usersService.createCustomerUser(user),
    onMutate: () => ({ successMessage: 'User created' }),
  });
};

export const useCreateUserOrReturnId = () => {
  return useMutation({
    mutationKey: [QUERY_KEY],
    mutationFn: (user: CreateCustomerUserDto) => usersService.createOrReturnId(user),
    onMutate: () => ({ successMessage: 'User received' }),
  });
};

export const usePartnerSubTypes = () => {
  return useQuery({
    queryKey: [PARTNER_SUB_TYPES_QUERY_KEY],
    queryFn: () => usersService.getPartnerSubTypes(),
  });
};

export const usePartnerSubType = (
  id: MaybeRefOrGetter<string>,
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  return useQuery({
    queryKey: [PARTNER_SUB_TYPES_QUERY_KEY, toRef(id)],
    queryFn: () => usersService.getPartnerSubType(toValue(id)),
    enabled,
  });
};

export const useCreatePartnerSubType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [PARTNER_SUB_TYPES_QUERY_KEY],
    mutationFn: (subType: CreatePartnerSubTypeDto) => usersService.createPartnerSubType(subType),
    onMutate: () => ({ successMessage: 'Partner subtype created' }),
    onSuccess: async (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(result, variables, onMutateResult, context);
      await queryClient.invalidateQueries({ queryKey: [PARTNER_SUB_TYPES_QUERY_KEY] });
    },
  });
};

export const useUpdatePartnerSubType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [PARTNER_SUB_TYPES_QUERY_KEY],
    mutationFn: ({ id, subType }: { id: string; subType: UpdatePartnerSubTypeDto }) =>
      usersService.updatePartnerSubType(id, subType),
    onMutate: () => ({ successMessage: 'Partner subtype updated' }),
    onSuccess: async (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(result, variables, onMutateResult, context);
      await queryClient.invalidateQueries({ queryKey: [PARTNER_SUB_TYPES_QUERY_KEY] });
      await queryClient.invalidateQueries({
        queryKey: [PARTNER_SUB_TYPES_QUERY_KEY, variables.id],
      });
      queryClient.setQueryData([PARTNER_SUB_TYPES_QUERY_KEY, variables.id], result);
    },
  });
};
